import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, OAuthProvider, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

/// Sans configuration, le site doit rester consultable : seuls l'achat et l'espace
/// compte se désactivent. Un écran blanc pour une clé manquante serait absurde.
export const isFirebaseConfigured = Boolean(config.apiKey && config.projectId)

export const app: FirebaseApp | null = isFirebaseConfigured ? initializeApp(config) : null
export const measurementId: string | null = config.measurementId ?? null

const authInstance: Auth | null = app ? getAuth(app) : null
const dbInstance: Firestore | null = app ? getFirestore(app) : null
const functionsInstance: Functions | null = app ? getFunctions(app, 'us-central1') : null

/**
 * Accesseurs plutôt qu'exports nullables.
 *
 * Chaque appelant est déjà protégé par `isFirebaseConfigured` ; forcer un
 * `as Auth` mentirait au compilateur et masquerait un vrai null le jour où une
 * garde saute. Lever une erreur explicite dit la vérité et échoue bruyamment.
 */
function required<T>(value: T | null, what: string): T {
  if (!value) throw new Error(`Firebase n'est pas configuré : ${what} indisponible.`)
  return value
}

export const requireAuth = (): Auth => required(authInstance, 'Auth')
export const requireDb = (): Firestore => required(dbInstance, 'Firestore')
export const requireFunctions = (): Functions => required(functionsInstance, 'Functions')

export const googleProvider = new GoogleAuthProvider()
export const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

/// Sign in with Apple demande une configuration séparée côté Apple Developer.
/// Tant qu'elle n'est pas faite, afficher le bouton ne produirait qu'une erreur.
export const appleSignInEnabled = import.meta.env.VITE_ENABLE_APPLE_SIGNIN === 'true'

export const downloadURL = import.meta.env.VITE_DOWNLOAD_URL ?? '#'
export const priceLabel = import.meta.env.VITE_PRICE_LABEL ?? '€25'
